import { Button, Dropdown, Menu, Modal, Space, Tag, message } from "antd";
import { DeleteOutlined, DownOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ApiService from "../../../core/service/api-service";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import ReportBillingStatusEnum, {
    BILLING_STATUS_LIST,
    billingStatusColor,
    billingStatusKey,
} from "../../../enums/ReportBillingStatusEnum";
import "../../../core/components/data-grid/grid-dropdown.css";

export default function ReportBillingTools({ onOpenReconciliation }) {
    const { t } = useTranslation();
    const { selectedRows, queryModel, refreshDataGrid } = useDataGrid();
    const { hasPermission } = useUser();

    const [busy, setBusy] = useState(false);

    const canChange = hasPermission(PERMISSIONS.REPORT_BILLING_STATUS_CHANGE);
    const canBulkApply = hasPermission(PERMISSIONS.REPORT_BILLING_BULK_APPLY);
    const canFinalize = hasPermission(PERMISSIONS.REPORT_BILLING_FINALIZE);
    const canExcel = hasPermission(PERMISSIONS.REPORT_BILLING_EXCEL);
    const canReconcile = hasPermission(PERMISSIONS.REPORT_BILLING_RECONCILE);
    const canDeleteDuplicates = hasPermission(PERMISSIONS.REPORT_BILLING_DELETE_DUPLICATE);

    // Silme yalnız durumu Mükerrer olan kayıtlara uygulanır; sunucu da aynı süzmeyi yapar,
    // buradaki sayım kullanıcıya ONAY ekranında kaç kaydın işleneceğini doğru göstermek için.
    const selectedDuplicates = selectedRows
        .filter(row => (row.billingStatus ?? 0) === ReportBillingStatusEnum.DUPLICATE);

    const deleteDuplicateRequests = () => {
        if (selectedDuplicates.length === 0) {
            message.info(t('BILLING_NO_DUPLICATE_SELECTED'));
            return;
        }

        Modal.confirm({
            title: t('Delete Duplicate Reports'),
            content: t('BILLING_DELETE_DUPLICATE_CONFIRM', { count: selectedDuplicates.length }),
            okText: t('OK'),
            okButtonProps: { danger: true },
            cancelText: t('Cancel'),
            onOk: async () => {
                const response = await ApiService.ReportBilling.DeleteDuplicateRequests({
                    reportIdList: selectedDuplicates.map(row => row.id),
                });

                if (!response.success)
                    return;

                const skipped = response.data?.skipped?.length || 0;

                if (skipped > 0) {
                    message.warning(t('BILLING_DELETE_DUPLICATE_RESULT_SKIPPED', {
                        deleted: response.data.deleted, skipped,
                    }));
                }
                else {
                    message.success(t('BILLING_DELETE_DUPLICATE_RESULT', {
                        deleted: response.data?.deleted || 0,
                    }));
                }

                if (response.data?.logWriteFailed)
                    message.error(t('BILLING_LOG_WRITE_FAILED'));

                await refreshDataGrid();
            },
        });
    }

    // "Faturalandırıldı" hedefi ayrı (üst) izin ister — sunucu da aynı kontrolü yapar.
    const allowedStatuses = BILLING_STATUS_LIST
        .filter(status => status !== ReportBillingStatusEnum.BILLED || canFinalize);

    const showResult = (result) => {
        const skipped = result?.skipped?.length || 0;

        if (skipped > 0) {
            message.warning(t('BILLING_BULK_RESULT_SKIPPED', { updated: result.updated, skipped }));
        }
        else {
            message.success(t('BILLING_BULK_RESULT', { updated: result?.updated || 0 }));
        }

        // Durum yazıldı ama mali iz yazılamadıysa sessiz geçilmez.
        if (result?.logWriteFailed) {
            message.error(t('BILLING_LOG_WRITE_FAILED'));
        }
    }

    // Seçili satırlar: istemcinin GÖRDÜĞÜ durum expectedStatus olarak gider; arada değişenler
    // sunucuda atlanır ve "işlenemedi" olarak geri döner.
    const applyToSelection = (newStatus) => {
        if (selectedRows.length === 0) {
            message.info(t('BILLING_NO_SELECTION'));
            return;
        }

        Modal.confirm({
            title: t('Change Billing Status'),
            content: t('BILLING_BULK_CONFIRM', {
                count: selectedRows.length,
                status: t(billingStatusKey(newStatus)),
            }),
            okText: t('OK'),
            cancelText: t('Cancel'),
            onOk: async () => {
                const response = await ApiService.ReportBilling.UpdateStatus({
                    newStatus,
                    items: selectedRows.map(row => ({
                        reportId: row.id,
                        expectedStatus: row.billingStatus ?? 0,
                    })),
                });

                if (response.success) {
                    showResult(response.data);
                    await refreshDataGrid();
                }
            },
        });
    }

    // Filtre sonucunun tamamı: onay diyaloğundaki adet TAVANLI sayaçtan değil, exact sayımdan gelir —
    // mali onay ekranında "500+" gösterilemez.
    const applyToFilter = async (newStatus) => {
        setBusy(true);
        const countResponse = await ApiService.ReportBilling.GetDataGridCount(queryModel, true);
        setBusy(false);

        if (!countResponse.success)
            return;

        const count = countResponse.data?.count || 0;

        if (count === 0) {
            message.info(t('BILLING_BULK_RESULT', { updated: 0 }));
            return;
        }

        Modal.confirm({
            title: t('Apply To All Filtered'),
            content: t('BILLING_BULK_CONFIRM_FILTER', {
                count,
                status: t(billingStatusKey(newStatus)),
            }),
            okText: t('OK'),
            cancelText: t('Cancel'),
            onOk: async () => {
                const response = await ApiService.ReportBilling.UpdateStatusByFilter({
                    filter: queryModel.filter,
                    newStatus,
                });

                if (response.success) {
                    showResult(response.data);
                    await refreshDataGrid();
                }
            },
        });
    }

    // Finalizasyon: dönemdeki "Mutabakat Yapıldı" raporları "Faturalandırıldı"ya geçirir.
    const finalize = async () => {
        setBusy(true);
        const countResponse = await ApiService.ReportBilling.GetDataGridCount({
            ...queryModel,
            filter: {
                ...queryModel.filter,
                billingStatusList: [ReportBillingStatusEnum.RECONCILED],
            },
        }, true);
        setBusy(false);

        if (!countResponse.success)
            return;

        const count = countResponse.data?.count || 0;

        if (count === 0) {
            message.info(t('BILLING_BULK_RESULT', { updated: 0 }));
            return;
        }

        Modal.confirm({
            title: t('Finalize Billing'),
            content: (
                <div>
                    <div style={{ marginBottom: 8 }}>{t('BILLING_FINALIZE_HINT')}</div>
                    <div>
                        {t('BILLING_BULK_CONFIRM_FILTER', {
                            count,
                            status: t(billingStatusKey(ReportBillingStatusEnum.BILLED)),
                        })}
                    </div>
                </div>
            ),
            okText: t('OK'),
            cancelText: t('Cancel'),
            onOk: async () => {
                const response = await ApiService.ReportBilling.UpdateStatusByFilter({
                    filter: {
                        ...queryModel.filter,
                        billingStatusList: [ReportBillingStatusEnum.RECONCILED],
                    },
                    expectedStatuses: [ReportBillingStatusEnum.RECONCILED],
                    newStatus: ReportBillingStatusEnum.BILLED,
                });

                if (response.success) {
                    showResult(response.data);
                    await refreshDataGrid();
                }
            },
        });
    }

    const statusMenu = (onSelect) => (
        <Menu>
            {allowedStatuses.map(status => (
                <Menu.Item key={status} onClick={() => onSelect(status)}>
                    <Tag color={billingStatusColor(status)}>{t(billingStatusKey(status))}</Tag>
                </Menu.Item>
            ))}
        </Menu>
    );

    const downloadExcel = async () => {
        await ApiService.ReportBilling.GetExcel(queryModel);
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Space>
                {canChange && (
                    <Dropdown
                        disabled={selectedRows.length === 0}
                        trigger={'click'}
                        overlayClassName={'grid-dropdown-overlay'}
                        overlay={statusMenu(applyToSelection)}
                    >
                        <Button size={'small'} type={'primary'}>
                            {t('Change Billing Status')}
                            {selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
                            <DownOutlined />
                        </Button>
                    </Dropdown>
                )}

                {canBulkApply && (
                    <Dropdown
                        trigger={'click'}
                        overlayClassName={'grid-dropdown-overlay'}
                        overlay={statusMenu(applyToFilter)}
                    >
                        <Button size={'small'} loading={busy}>
                            {t('Apply To All Filtered')}<DownOutlined />
                        </Button>
                    </Dropdown>
                )}

                {canReconcile && (
                    <Button size={'small'} onClick={onOpenReconciliation}>
                        {t('Reconciliation')}
                    </Button>
                )}

                {canDeleteDuplicates && (
                    <Dropdown
                        disabled={selectedRows.length === 0}
                        trigger={'click'}
                        overlayClassName={'grid-dropdown-overlay'}
                        overlay={(
                            <Menu>
                                <Menu.Item
                                    key={'delete-duplicates'}
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={deleteDuplicateRequests}
                                >
                                    {t('Delete Duplicate Reports')}
                                    {selectedDuplicates.length > 0 ? ` (${selectedDuplicates.length})` : ''}
                                </Menu.Item>
                            </Menu>
                        )}
                    >
                        <Button size={'small'}>
                            {t('Operations')}<DownOutlined />
                        </Button>
                    </Dropdown>
                )}

                {canFinalize && canBulkApply && (
                    <Button size={'small'} loading={busy} onClick={finalize}>
                        {t('Finalize Billing')}
                    </Button>
                )}
            </Space>

            {canExcel && (
                <Button size={'small'} onClick={downloadExcel} color={'green'} variant={'solid'}>
                    {t('Excel')}
                </Button>
            )}
        </div>
    )
}
